import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import {
  fetchAuthConfig,
  initiateKeycloakLogin,
  handleKeycloakCallback,
  fetchKeycloakLogoutUrl,
  type AuthConfig,
} from '../services/keycloakAuth';

interface User {
  id: number;
  email: string;
  name: string;
  role: 'admin' | 'viewer';
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  authMode: 'local' | 'oidc' | null;
  keycloakAdminUrl: string | null;
  keycloakAccountUrl: string | null;
  login: (email: string, password: string) => Promise<void>;
  loginWithKeycloak: () => Promise<void>;
  handleOidcCallback: (code: string, state: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  apiCall: (url: string, options?: RequestInit) => Promise<Response>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_KEY = 'kern_feedback_token';
const USER_KEY = 'kern_feedback_user';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authConfig, setAuthConfig] = useState<AuthConfig | null>(null);

  useEffect(() => {
    const init = async () => {
      // Restore session from localStorage
      const storedToken = localStorage.getItem(TOKEN_KEY);
      const storedUser = localStorage.getItem(USER_KEY);

      if (storedToken && storedUser) {
        try {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
        } catch {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
        }
      }

      // Fetch auth config
      try {
        const cfg = await fetchAuthConfig();
        setAuthConfig(cfg);
      } catch {
        // Fallback to local mode if config endpoint fails
        setAuthConfig({ authMode: 'local' });
      }

      setIsLoading(false);
    };

    init();
  }, []);

  const login = async (email: string, password: string) => {
    const response = await fetch('/api/v1/auth/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Login fehlgeschlagen');
    }

    const data = await response.json();

    setToken(data.token);
    setUser(data.user);

    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
  };

  const loginWithKeycloak = async () => {
    await initiateKeycloakLogin();
  };

  const handleOidcCallback = async (code: string, state: string) => {
    const data = await handleKeycloakCallback(code, state);

    setToken(data.token);
    setUser(data.user as User);

    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
  };

  const logout = useCallback(async () => {
    const isOidc = authConfig?.authMode === 'oidc';

    setToken(null);
    setUser(null);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);

    if (isOidc) {
      try {
        const logoutUrl = await fetchKeycloakLogoutUrl();
        window.location.href = logoutUrl;
        return;
      } catch {
        // Fall through to local logout
      }
    }
  }, [authConfig]);

  // Helper function for authenticated API calls
  const apiCall = useCallback(
    async (url: string, options: RequestInit = {}): Promise<Response> => {
      const headers = new Headers(options.headers);
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      if (!headers.has('Content-Type') && options.body && typeof options.body === 'string') {
        headers.set('Content-Type', 'application/json');
      }

      const response = await fetch(url, {
        ...options,
        headers,
      });

      // Auto-logout on 401 and redirect to login (which auto-redirects to Keycloak in OIDC mode)
      if (response.status === 401) {
        setToken(null);
        setUser(null);
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);

        if (authConfig?.authMode === 'oidc') {
          try {
            await initiateKeycloakLogin();
            return response;
          } catch {
            // Fall through — ProtectedRoute will redirect to /login
          }
        }
      }

      return response;
    },
    [token, authConfig]
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isLoading,
        authMode: authConfig?.authMode ?? null,
        keycloakAdminUrl: authConfig?.keycloak?.adminUrl ?? null,
        keycloakAccountUrl: authConfig?.keycloak?.accountUrl ?? null,
        login,
        loginWithKeycloak,
        handleOidcCallback,
        logout,
        isAuthenticated: !!token && !!user,
        isAdmin: user?.role === 'admin',
        apiCall,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
